import { type RefObject, useCallback, useEffect, useState } from 'react';

type PlayerState = {
  isMuted: boolean;
  isPlaying: boolean;
  progress: number;
  speed: number;
};

export const useVideoPlayer = ({
  onVideoEnded,
  videoElement,
}: {
  onVideoEnded?: () => void;
  videoElement: RefObject<HTMLVideoElement>;
}) => {
  const [playerState, setPlayerState] = useState<PlayerState>({
    isMuted: false,
    isPlaying: false,
    progress: 0,
    speed: 1,
  });

  const togglePlay = useCallback(() => {
    setPlayerState((currentPlayerState) => {
      return {
        ...currentPlayerState,
        isPlaying: !currentPlayerState.isPlaying,
      };
    });
  }, []);

  const updateProgress = useCallback(() => {
    const player = videoElement.current;

    if (!player) {
      return;
    }

    setPlayerState((currentPlayerState) => ({
      ...currentPlayerState,
      progress: Math.round((player.currentTime / player.duration) * 100),
    }));
  }, [videoElement]);

  useEffect(() => {
    const player = videoElement.current;

    if (!player) {
      return;
    }

    if (playerState.isPlaying) {
      void player.play();
    } else {
      player.pause();
    }
  }, [playerState.isPlaying, videoElement]);

  useEffect(() => {
    const player = videoElement.current;

    if (!player) {
      return () => {};
    }

    if (!onVideoEnded) {
      return () => {};
    }

    player.addEventListener('ended', onVideoEnded, false);
    player.addEventListener('timeupdate', updateProgress, false);

    return () => {
      player.removeEventListener('ended', onVideoEnded, false);
      player.removeEventListener('timeupdate', updateProgress, false);
    };
  }, [onVideoEnded, videoElement, playerState.isPlaying, updateProgress]);

  const toggleMute = useCallback(() => {
    setPlayerState((currentPlayerState) => {
      return {
        ...currentPlayerState,
        isMuted: !currentPlayerState.isMuted,
      };
    });
  }, []);

  useEffect(() => {
    const player = videoElement.current;

    if (!player) {
      return;
    }

    if (playerState.isMuted) {
      player.muted = true;
    } else {
      player.muted = false;
    }
  }, [playerState.isMuted, videoElement]);

  return {
    playerState,
    toggleMute,
    togglePlay,
  };
};
